import { useEffect, useState } from "react";
import { Download, X, Sparkles } from "lucide-react";

// Small, unobtrusive "Add to Home Screen" prompt.
//   - On Android/Chrome/Edge the browser fires `beforeinstallprompt`; we
//     hold that event and let the user trigger it from our own button so
//     the prompt lands in a moment the user actually cares about.
//   - On iOS Safari there is no programmatic install API, so we show a
//     one-time hint explaining Share → Add to Home Screen.
//   - We dismiss for 30 days once closed to avoid nagging.
const DISMISS_KEY = "stagecall:install-dismissed-at";
const DISMISS_MS = 30 * 24 * 60 * 60 * 1000;

const isStandalone = () =>
  window.matchMedia?.("(display-mode: standalone)").matches ||
  window.navigator.standalone === true;

const isIos = () => /iphone|ipad|ipod/i.test(window.navigator.userAgent);

export default function InstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [showIosHint, setShowIosHint] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (isStandalone()) return;
    const dismissedAt = Number(localStorage.getItem(DISMISS_KEY) || 0);
    if (dismissedAt && Date.now() - dismissedAt < DISMISS_MS) return;

    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferred(e);
      setVisible(true);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);

    // iOS never fires beforeinstallprompt — show a static hint instead.
    if (isIos()) {
      const t = setTimeout(() => {
        setShowIosHint(true);
        setVisible(true);
      }, 4000);
      return () => {
        clearTimeout(t);
        window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      };
    }
    return () => window.removeEventListener("beforeinstallprompt", onBeforeInstall);
  }, []);

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setVisible(false);
  };

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    try {
      await deferred.userChoice;
    } catch { /* ignore */ }
    setDeferred(null);
    setVisible(false);
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
  };

  if (!visible) return null;

  return (
    <div
      data-testid="pwa-install-prompt"
      className="fixed z-[70] bottom-4 left-1/2 -translate-x-1/2 max-w-md w-[92%] rounded-xl border border-white/10 bg-[#121212]/95 backdrop-blur px-4 py-3 shadow-2xl flex items-center gap-3"
    >
      <div className="w-9 h-9 rounded-md grid place-items-center bg-gold/10 border border-gold/30 shrink-0">
        <Sparkles className="w-4 h-4 text-gold" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm text-white font-medium">
          {showIosHint ? "Install StageCall on your iPhone" : "Install StageCall"}
        </div>
        <div className="text-[11px] text-zinc-400 mt-0.5 truncate">
          {showIosHint
            ? "Tap Share, then Add to Home Screen for a full-screen app."
            : "Faster launch, offline access, and full-screen mode on your phone."}
        </div>
      </div>
      {!showIosHint && (
        <button
          data-testid="pwa-install-btn"
          onClick={install}
          className="inline-flex items-center gap-1 h-8 px-3 rounded-md text-xs font-semibold"
          style={{ backgroundColor: "#F59E0B", color: "#0A0A0A" }}
        >
          <Download className="w-3.5 h-3.5" /> Install
        </button>
      )}
      <button
        data-testid="pwa-install-dismiss"
        onClick={dismiss}
        aria-label="Dismiss"
        className="w-8 h-8 rounded-md text-zinc-400 hover:text-white hover:bg-white/5 grid place-items-center"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}
